import { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { alpha, makeStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import InputAdornment from '@material-ui/core/InputAdornment';
import Radio from '@material-ui/core/Radio';
import { ReactComponent as DropdownIcon } from '../../../assets/icons/arrow-drop-down.svg';
import { ReactComponent as DefaultIcon } from '../../../assets/icons/notes.svg';
import { ReactComponent as SortIndicator } from '../../../assets/icons/sort-arrow-dark.svg';
import { useCustomEffect } from '../../../utility/hooks';
import { ellipsis } from '../../../constants/helperCssRules';
import TextBoxWithTooltip from '../textBoxWithTooltip';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  main: {
    position: 'relative',
    height: 40,
  },
  dropdownIcon: {
    top: 'calc(50% - 8px)',
    right: 7,
  },
  inputRoot: {
    cursor: 'default',
    maxWidth: 245,
  },
  selectRoot: {
    padding: spacing(2, 0),
    '& $radioButton': {
      display: 'none',
    },
    '& $menuItemLabel': {
      fontSize: 14,
      lineHeight: '18px',
    },
  },
  select: {
    '&:focus': {
      backgroundColor: 'transparent',
    },
  },
  name: {
    userSelect: 'none',
  },
  filterIconWrapper: {
    marginRight: spacing(1),
  },
  filterIcon: {
    marginRight: spacing(2),
    marginBottom: spacing(0.25),
  },
  sortIcon: {
    display: 'block',
    height: 7,
    width: 8,
    marginRight: spacing(2),
    transition: 'transform .3s ease',
  },
  reverse: {
    '-webkit-transform': 'rotate(180deg)',
    '-moz-transform': 'rotate(180deg)',
    '-o-transform': 'rotate(180deg)',
    '-ms-transform': 'rotate(180deg)',
    transform: 'rotate(180deg)',
    transition: 'transform .3s ease',
  },
  paper: {
    backgroundColor: primary.white,
    boxSizing: 'border-box',
    border: `1px solid ${primary.bluish6}`,
    boxShadow: 'none',
    borderRadius: 4,
    filter: `drop-shadow(0px 4px 10px ${alpha(primary.black, 0.2)})`,
    marginTop: spacing(2),
    minWidth: '245px !important',
    '&::-webkit-scrollbar': {
      borderRadius: 4,
      background: 'rgb(201,212,218, .44)',
      height: 'calc(100% - 6px)',
      width: 4,
    },
    '&::-webkit-scrollbar-track': {
      borderRadius: 4,
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: 4,
      background: primary.blue2,
    },
  },
  menu: {
    padding: 0,
    width: 'calc(100% + 4px) !important',
  },
  menuItem: {
    padding: spacing(2, 4),
    '&:hover': {
      backgroundColor: primary.bluish8,
    },
  },
  menuItemActive: {
    backgroundColor: `${primary.bluish8} !important`,
  },
  menuItemContent: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    '& > div': {
      display: 'flex',
    },
  },
  menuItemLabel: {
    flexGrow: 1,
    ...ellipsis(),
  },
  radioButton: {
    color: primary.bluish4,
    width: 24,
    height: 24,
  },
  radioChecked: {
    color: `${primary.bluish4} !important`,
  },
}));

const Filter = ({
  className = null,
  customPaperClass = null,
  name = '',
  options = [],
  value = 0,
  icon: Icon = null,
  isDisabled = false,
  isOrdering = false,
  onChange,
}) => {
  const classes = useStyles();
  const menuRef = useRef(null);

  const [anchorEl, setAnchorEl] = useState(null);
  const isDescending = typeof value === 'string' && value.startsWith('-');

  useCustomEffect(() => {
    setAnchorEl(menuRef?.current);
  });

  const handleChange = e => {
    if (value !== e.target.value) {
      onChange(e.target.value);
    }
  };

  const renderStartIcon = () => {
    if (isOrdering) {
      return (
        <SortIndicator
          className={classNames(classes.sortIcon, {
            [classes.reverse]: isDescending,
          })}
        />
      );
    }

    if (Icon) {
      return <Icon className={classes.filterIcon} />;
    }

    return <DefaultIcon className={classes.filterIcon} />;
  };

  const renderAdornment = () => (
    <InputAdornment
      classes={{ root: classes.filterIconWrapper }}
      position="start"
    >
      {renderStartIcon()}
      {!isOrdering && name && (
        <TextBoxWithTooltip
          className={classes.name}
          variant="subtitle2"
          text={`${name}:`}
        />
      )}
    </InputAdornment>
  );

  return (
    <div ref={menuRef} className={classNames(classes.main, className)}>
      <TextField
        value={value}
        InputProps={{
          classes: { root: classes.inputRoot },
          startAdornment: renderAdornment(),
          disabled: isDisabled,
        }}
        SelectProps={{
          classes: {
            root: classes.selectRoot,
            select: classes.select,
            icon: classes.dropdownIcon,
          },
          IconComponent: DropdownIcon,
          MenuProps: {
            classes: {
              list: classes.menu,
              paper: classNames(classes.paper, customPaperClass),
            },
            anchorEl,
            getContentAnchorEl: null,
            anchorOrigin: {
              horizontal: 'left',
              vertical: 'bottom',
            },
          },
        }}
        onChange={handleChange}
        select
      >
        {options.map(option => (
          <MenuItem
            key={option.id}
            classes={{
              root: classNames(classes.menuItem, {
                [classes.menuItemActive]: option.id === value,
              }),
            }}
            value={option.id}
          >
            <span className={classes.menuItemContent}>
              <TextBoxWithTooltip
                className={classes.menuItemLabel}
                component="span"
                text={option.name}
              />
              <Radio
                classes={{
                  root: classes.radioButton,
                  checked: classes.radioChecked,
                }}
                checked={option.id === value}
              />
            </span>
          </MenuItem>
        ))}
      </TextField>
    </div>
  );
};

Filter.propTypes = {
  className: PropTypes.string,
  customPaperClass: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  name: PropTypes.string,
  isDisabled: PropTypes.bool,
  isOrdering: PropTypes.bool,
  options: PropTypes.arrayOf(PropTypes.shape({})),
};

export default Filter;
